// Ambient jukebox, synthesised in-browser via Web Audio — no audio files to ship
// or license, and no copyrighted loops. It plays an actual chord progression: a
// soft sustained pad, a plucked bass on the downbeat, and a gentle melody picking
// notes from the current chord over a fixed rhythm (with rests), through a lowpass
// filter and a feedback delay for warmth and space. Chord progressions are
// consonant by construction, so it stays musical however it loops.
//
// Module-level so it keeps playing between rooms/screens. Off by default (autoplay
// policy + courtesy); the ♪ toggle starts it on a user gesture, preference stored.

import type { JukeboxConfig } from './world';

const PREF_KEY = 'tpiap:jukebox';
const BEATS_PER_BAR = 4;
// Melody rhythm over a bar (indices into the chord; -1 = rest). Two bars, gentle.
const MELODY = [0, -1, 2, -1, 1, -1, 2, 0, -1, 2, -1, 1, 4, -1, 2, -1];

interface Engine {
  ctx: AudioContext;
  master: GainNode;
  wet: DelayNode;
  timer: number;
}
let engine: Engine | null = null;

export function jukeboxOn(): boolean {
  return engine !== null;
}

export function jukeboxPreferred(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === 'on';
  } catch {
    return false;
  }
}

function remember(on: boolean) {
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* ignore */
  }
}

export function stopJukebox(): void {
  if (!engine) return;
  const { ctx, master, timer } = engine;
  window.clearTimeout(timer);
  master.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
  window.setTimeout(() => ctx.close().catch(() => {}), 400);
  engine = null;
}

export function startJukebox(cfg: JukeboxConfig): void {
  if (engine) return;
  const AC: typeof AudioContext =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AC) return;
  const ctx = new AC();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  const master = ctx.createGain();
  master.gain.value = cfg.gain;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2000;
  master.connect(lp);
  lp.connect(ctx.destination);

  // Feedback delay for space; melody/pad send a little into it.
  const wet = ctx.createDelay(1.0);
  wet.delayTime.value = (cfg.beatMs * 3) / 1000;
  const fb = ctx.createGain();
  fb.gain.value = 0.3;
  wet.connect(fb);
  fb.connect(wet);
  wet.connect(lp);

  const freqOf = (semi: number) => cfg.root * Math.pow(2, semi / 12);

  /** One note: osc → env → (dry master) + (wet delay). */
  const voice = (semi: number, type: OscillatorType, at: number, dur: number, peak: number, sendWet: number) => {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freqOf(semi);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.linearRampToValueAtTime(peak, at + Math.min(0.05, dur * 0.2));
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    o.connect(g);
    g.connect(master);
    if (sendWet > 0) {
      const s = ctx.createGain();
      s.gain.value = sendWet;
      g.connect(s);
      s.connect(wet);
    }
    o.start(at);
    o.stop(at + dur + 0.05);
  };

  let beat = 0;
  const stepSec = cfg.beatMs / 1000;
  const tick = () => {
    if (!engine) return;
    const t = ctx.currentTime + 0.02;
    const chord = cfg.chords[Math.floor(beat / BEATS_PER_BAR) % cfg.chords.length];
    const inBar = beat % BEATS_PER_BAR;

    if (inBar === 0) {
      // Pad: whole chord, sustained across the bar, soft.
      const barDur = stepSec * BEATS_PER_BAR;
      chord.forEach((semi) => voice(semi, cfg.wave, t, barDur * 0.98, 0.16, 0.15));
      // Bass: chord root, an octave down, plucked.
      voice(chord[0] - 12, 'triangle', t, stepSec * 1.6, 0.5, 0.05);
    }
    // Melody: a chord tone an octave up, on the rhythm pattern.
    const m = MELODY[beat % MELODY.length];
    if (m >= 0) {
      const semi = chord[m % chord.length] + 12;
      voice(semi, cfg.wave, t, stepSec * 0.9, 0.28, 0.35);
    }

    beat++;
    engine.timer = window.setTimeout(tick, cfg.beatMs);
  };

  engine = { ctx, master, wet, timer: 0 };
  engine.timer = window.setTimeout(tick, 0);
}

/** Toggle and remember the preference. Returns the new on/off state. */
export function toggleJukebox(cfg: JukeboxConfig): boolean {
  if (engine) {
    stopJukebox();
    remember(false);
    return false;
  }
  startJukebox(cfg);
  remember(true);
  return true;
}
